/**
 * Accessibility utilities for screen reader announcements and other a11y features
 */

let announcer: HTMLDivElement | null = null;

/**
 * Initialize the live region announcer element
 */
export function initializeAnnouncer(): void {
  if (typeof document === 'undefined') return;
  if (announcer) return; // Already initialized

  announcer = document.createElement('div');
  announcer.setAttribute('role', 'status');
  announcer.setAttribute('aria-live', 'polite');
  announcer.setAttribute('aria-atomic', 'true');
  announcer.className = 'sr-only';
  announcer.id = 'a11y-announcer';
  document.body.appendChild(announcer);
}

/**
 * Announce a message to screen readers
 * @param message - The message to announce
 * @param priority - 'polite' for non-urgent, 'assertive' for urgent messages
 */
export function announceToScreenReader(
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
): void {
  if (!announcer) {
    initializeAnnouncer();
  }
  if (!announcer) return;

  announcer.setAttribute('aria-live', priority);

  // Clear and set to trigger announcement
  announcer.textContent = '';

  // Use requestAnimationFrame to ensure the DOM update is processed
  requestAnimationFrame(() => {
    if (announcer) {
      announcer.textContent = message;
    }
  });
}

/**
 * Generate a unique ID for accessibility purposes
 */
let idCounter = 0;
export function generateA11yId(prefix = 'a11y'): string {
  return `${prefix}-${++idCounter}`;
}

/**
 * Check if an element is currently visible (not hidden)
 */
export function isElementVisible(element: HTMLElement): boolean {
  return !!(
    element.offsetWidth ||
    element.offsetHeight ||
    element.getClientRects().length
  );
}

/**
 * Get all focusable elements within a container
 */
export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  const elements = Array.from(
    container.querySelectorAll<HTMLElement>(selector)
  );

  return elements.filter(isElementVisible);
}

/**
 * Move focus to the first focusable element in a container
 */
export function focusFirstElement(container: HTMLElement): boolean {
  const focusable = getFocusableElements(container);
  const firstElement = focusable[0];
  if (firstElement) {
    firstElement.focus();
    return true;
  }
  return false;
}

/**
 * Check color contrast ratio (simplified version)
 * Returns the contrast ratio between two colors
 */
export function getContrastRatio(
  foreground: { r: number; g: number; b: number },
  background: { r: number; g: number; b: number }
): number {
  const getLuminance = (rgb: { r: number; g: number; b: number }) => {
    const toLinear = (c: number) => {
      const sRGB = c / 255;
      return sRGB <= 0.03928
        ? sRGB / 12.92
        : Math.pow((sRGB + 0.055) / 1.055, 2.4);
    };
    const rLinear = toLinear(rgb.r);
    const gLinear = toLinear(rgb.g);
    const bLinear = toLinear(rgb.b);
    return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
  };

  const l1 = getLuminance(foreground);
  const l2 = getLuminance(background);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Check if a key event is an activation key (Enter or Space)
 */
export function isActivationKey(event: KeyboardEvent): boolean {
  return event.key === 'Enter' || event.key === ' ';
}

/**
 * Create an aria-describedby ID reference
 */
export function createDescribedBy(
  ...ids: (string | null | undefined)[]
): string | undefined {
  const validIds = ids.filter(Boolean);
  return validIds.length > 0 ? validIds.join(' ') : undefined;
}
